import React, { useCallback, useState, useEffect, useRef, createRef } from 'react';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  X,
  Monitor,
  Zap,
  Trash2,
  Database,
  Upload,
  Wifi,
  WifiOff,
  Network,
  StopCircle,
  KeyRound,
  Bot,
  Columns2,
  Square,
  MoreVertical
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CardSquare, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { type TerminalSession, type TabbedTerminalProps, type ITerminal } from '@chainlit/react-client';
import { terminalState, sessionState, pendingTerminalCommandState, loadingState } from '@chainlit/react-client';
import { pendingTerminalHistoriesState } from '@/state/project';
import { useIsMobile } from '@/hooks/use-mobile';

import Terminal from './Terminal';

const INITIAL_USER_TERMINAL_ID = 'user-terminal-1';

const SPLIT_MIN = 20;
const SPLIT_MAX = 80;

/*
 * `isAiTerminal` marks a tab spawned by the AI (vs the interactive user
 * terminal). It is fixed at creation and, unlike `isReadOnly`, does NOT flip on
 * takeover — the render key keys off it so taking over an AI tab never remounts
 * the Terminal (which would drop the SSH session + scrollback, #1126).
 */
type TabSession = TerminalSession & { isAiTerminal?: boolean };

const TabbedTerminal: React.FC<TabbedTerminalProps> = ({
  wsUrl = '',
  className
}) => {
  const isMobile = useIsMobile();
  const [sessions, setSessions] = useState<TabSession[]>([
    {
      id: INITIAL_USER_TERMINAL_ID,
      name: 'Terminal 1',
      wsUrl,
      isConnected: false,
      isConnecting: false,
      isReadOnly: false
    }
  ]);
  const [activeTab, setActiveTab] = useState(INITIAL_USER_TERMINAL_ID);
  const [activeAiTerminalId, setActiveAiTerminalId] = useState<string | null>(null);
  const [portMappings, setPortMappings] = useState<Record<string, string>>({});
  const [isPortsPopoverOpen, setIsPortsPopoverOpen] = useState(false);
  const HISTORY_MAX_N = 50;
  const [historyScope, setHistoryScope] = useState<'last' | 'all' | 'custom'>('last');
  const [historyCustomN, setHistoryCustomN] = useState<string>('5');
  const [isHistoryPopoverOpen, setIsHistoryPopoverOpen] = useState(false);

  const parsedCustomN = (() => {
    const trimmed = historyCustomN.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    if (!Number.isInteger(n)) return null;
    if (n < 1 || n > HISTORY_MAX_N) return null;
    return n;
  })();
  const customInvalid = historyScope === 'custom' && parsedCustomN === null;
  const sendDisabled = customInvalid;
  const [isSplit, setIsSplit] = useState(false);
  const [splitTabId, setSplitTabId] = useState<string | null>(null);
  const [splitRatio, setSplitRatio] = useState(50);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  const terminalRefs = useRef<{ [id: string]: React.RefObject<any> }>({});
  sessions.forEach(s => {
    if (!terminalRefs.current[s.id]) {
      terminalRefs.current[s.id] = createRef();
    }
  });

  const aiTerminalConnectRef = useRef<(() => void) | null>(null);
  const aiTerminalConnectedRef = useRef<boolean>(false);
  const prevSessionIdRef = useRef<string | undefined>();

  const terminals = useRecoilValue(terminalState) as unknown as Record<string, ITerminal>;
  const session = useRecoilValue(sessionState);
  const [pendingCommand, setPendingCommand] = useRecoilState(pendingTerminalCommandState);
  const isTaskStreaming = useRecoilValue(loadingState);
  const setPendingHistories = useSetRecoilState(pendingTerminalHistoriesState);
  const { pathname } = useLocation();
  const routeThreadId = pathname.match(/^\/(?:thread|share)\/([^/]+)/)?.[1];

  const prevThreadIdRef = useRef<string | undefined>(routeThreadId);
  const [userTerminalEpoch, setUserTerminalEpoch] = useState(0);
  useEffect(() => {
    const prev = prevThreadIdRef.current;
    prevThreadIdRef.current = routeThreadId;
    // Refresh the interactive user terminal (fresh xterm + reconnect) when the
    // operator switches between two existing threads. The id is read from the
    // route path via useLocation (which resolves at any depth — useParams does
    // not here, since TabbedTerminal is portaled above the router Outlet), so it
    // changes A -> B synchronously with no undefined dip. Requiring both ids
    // defined + different also skips the new-chat -> thread-creation transition,
    // so the first message never wipes the terminal mid-session (#1075). AI tabs
    // reset via restore_terminal_session.
    if (prev && routeThreadId && prev !== routeThreadId) {
      setUserTerminalEpoch((e) => e + 1);
    }
  }, [routeThreadId]);

  const updateSessionStatus = useCallback((sessionId: string, updates: Partial<TerminalSession>) => {
    setSessions(prev =>
      prev.map(s => s.id === sessionId ? { ...s, ...updates } : s)
    );
  }, []);

  const setAiTerminalConnect = useCallback((connectFunction: () => void) => {
    aiTerminalConnectRef.current = connectFunction;
  }, []);

  /*
   * The backend no longer tracks a single "active AI terminal". Routing of
   * AI-issued commands is explicit via `target_id` or `terminal_id` in the
   * shell tool, so the frontend only needs to drive its own tab highlight.
   * The socket emit was removed to avoid the redundant round-trip.
   */

  useEffect(() => {
    const socket = session?.socket;
    if (!socket) return;

    const handler = (data: { terminal_id?: string; label?: string }) => {
      const terminalId = data?.terminal_id;
      if (!terminalId) return;

      setSessions((prev) => {
        if (prev.some((s) => s.id === terminalId)) {
          return prev;
        }
        const label = data.label?.trim();
        const fallbackCount = prev.filter((s) => s.isReadOnly).length + 1;
        const name = label ? label : `AI Terminal ${fallbackCount}`;
        const next: TabSession = {
          id: terminalId,
          name,
          wsUrl,
          isConnected: false,
          isConnecting: false,
          isReadOnly: true,
          isAiTerminal: true
        };
        return [...prev, next];
      });

      setActiveAiTerminalId(terminalId);
      setActiveTab(terminalId);
      aiTerminalConnectedRef.current = false;
      aiTerminalConnectRef.current = null;
    };

    socket.on('spawn_ai_terminal', handler);
    return () => {
      socket.off('spawn_ai_terminal', handler);
    };
  }, [session?.socket, wsUrl]);

  useEffect(() => {
    const socket = session?.socket;
    if (!socket) return;

    const restoreHandler = (data: {
      ai_terminals?: Array<{
        terminal_id: string;
        label?: string | null;
        target_id?: string | null;
        commands?: Array<{ command: string; output: string }>;
      }>;
    }) => {
      const incoming = Array.isArray(data?.ai_terminals) ? data.ai_terminals : [];

      setSessions((prev) => {
        const nonAi = prev.filter((s) => !s.isReadOnly);
        const restored: TabSession[] = incoming.map((t, idx) => ({
          id: t.terminal_id,
          name: (t.label && t.label.trim()) || `AI Terminal ${idx + 1}`,
          wsUrl,
          isConnected: false,
          isConnecting: false,
          isReadOnly: true,
          isAiTerminal: true
        }));
        return [...nonAi, ...restored];
      });

      const histories: Record<string, string> = {};
      incoming.forEach((t) => {
        if (!t.commands || t.commands.length === 0) return;
        const lines: string[] = [];
        t.commands.forEach((c) => {
          lines.push(`$ ${c.command}\r\n`);
          if (c.output) {
            for (const line of c.output.split('\n')) {
              lines.push(`${line}\r\n`);
            }
          }
        });
        histories[t.terminal_id] = lines.join('');
      });
      setPendingHistories(histories);

      if (incoming.length > 0) {
        const firstId = incoming[0].terminal_id;
        setActiveTab(firstId);
        setActiveAiTerminalId(firstId);
      } else {
        setActiveTab(INITIAL_USER_TERMINAL_ID);
        setActiveAiTerminalId(null);
      }

      aiTerminalConnectedRef.current = false;
      aiTerminalConnectRef.current = null;

      const incomingIds = new Set(incoming.map((t) => t.terminal_id));
      Object.keys(terminalRefs.current).forEach((id) => {
        if (id !== INITIAL_USER_TERMINAL_ID && !incomingIds.has(id)) {
          delete terminalRefs.current[id];
        }
      });

      console.log(
        `[TabbedTerminal] restore_terminal_session: reset to ${incoming.length} AI terminal(s)`
      );
    };

    socket.on('restore_terminal_session', restoreHandler);
    return () => {
      socket.off('restore_terminal_session', restoreHandler);
    };
  }, [session?.socket, wsUrl, setPendingHistories]);

  useEffect(() => {
    const currentSessionId = session?.socket?.id;

    if (prevSessionIdRef.current && currentSessionId && prevSessionIdRef.current !== currentSessionId) {
      console.log('[TabbedTerminal] Session changed, resetting connection tracking');
      aiTerminalConnectedRef.current = false;
      setSessions(prev => prev.map(s => ({
        ...s,
        isConnected: false,
        isConnecting: false
      })));
    }

    prevSessionIdRef.current = currentSessionId;
  }, [session?.socket?.id]);

  useEffect(() => {
    Object.entries(terminals).forEach(([_identifier, _terminalData]) => {
      const activeAiSession = sessions.find(s => s.id === activeAiTerminalId);
      if (!activeAiSession) return;

      if (!activeAiSession.isConnected &&
          !activeAiSession.isConnecting &&
          !aiTerminalConnectedRef.current &&
          typeof aiTerminalConnectRef.current === 'function') {
        aiTerminalConnectRef.current();
        aiTerminalConnectedRef.current = true;
        updateSessionStatus(activeAiSession.id, { isConnecting: true });
      }
    });
  }, [terminals, sessions, activeAiTerminalId, updateSessionStatus]);

  const addNewSession = useCallback(() => {
    const newId = (sessions.length + 1).toString();
    const newSession: TerminalSession = {
      id: newId,
      name: `Terminal ${newId}`,
      wsUrl,
      isConnected: false,
      isConnecting: false,
      isReadOnly: false
    };

    setSessions(prev => [...prev, newSession]);
    setActiveTab(newId);
  }, [sessions.length, wsUrl]);

  const handleSetActiveAiTerminal = useCallback((sessionId: string) => {
    setActiveAiTerminalId(sessionId);
    aiTerminalConnectedRef.current = false;
    aiTerminalConnectRef.current = null;
  }, []);

  const toggleSessionReadOnly = useCallback((sessionId: string) => {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId ? { ...s, isReadOnly: !s.isReadOnly } : s
      )
    );
  }, []);

  /**
   * Reclaims a user-controlled AI tab back for the AI when an approved
   * command targets it — without this, an approved command sits inert
   * until the operator manually clicks "Release back to AI".
   */
  const reclaimForAi = useCallback(
    (sessionId: string) => {
      const target = sessions.find((s) => s.id === sessionId);
      if (!target || target.isReadOnly) return;
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, isReadOnly: true } : s))
      );
      toast(`AI reclaimed control of ${target.name} to run the approved command`);
    },
    [sessions]
  );

  const removeSession = useCallback((sessionId: string) => {
    const targetSession = sessions.find(s => s.id === sessionId);
    if (!targetSession) return;

    if (targetSession.isReadOnly && sessionId === activeAiTerminalId) {
      const aiSessions = sessions.filter(s => s.isReadOnly);
      const nextAi = aiSessions.find(s => s.id !== sessionId);
      setActiveAiTerminalId(nextAi ? nextAi.id : null);
      aiTerminalConnectedRef.current = false;
      aiTerminalConnectRef.current = null;
    }

    if (sessions.length <= 1) return;

    setSessions(prev => prev.filter(s => s.id !== sessionId));
    if (splitTabId === sessionId) setSplitTabId(null);

    if (activeTab === sessionId) {
      const remaining = sessions.filter(s => s.id !== sessionId);
      if (remaining.length > 0) {
        setActiveTab(remaining[0].id);
      }
    }
  }, [sessions, activeTab, activeAiTerminalId, splitTabId]);

  const handleConnect = useCallback((sessionId: string) => {
    updateSessionStatus(sessionId, { isConnecting: true });

    setTimeout(() => {
      updateSessionStatus(sessionId, {
        isConnecting: false,
        isConnected: true
      });
    }, 1000);
  }, [updateSessionStatus]);

  const handleDisconnect = useCallback((sessionId: string) => {
    updateSessionStatus(sessionId, {
      isConnected: false,
      isConnecting: false
    });

    if (sessionId === activeAiTerminalId) {
      aiTerminalConnectedRef.current = false;
    }
  }, [updateSessionStatus, activeAiTerminalId]);

  const handleGetPortMappings = useCallback(() => {
    const activeSession = sessions.find(s => s.id === activeTab);
    if (!activeSession) return;

    const terminalRef = terminalRefs.current[activeSession.id];
    if (terminalRef?.current?.getPortMappings) {
      const mappings = terminalRef.current.getPortMappings();
      setPortMappings(mappings || {});
    }
  }, [sessions, activeTab]);

  useEffect(() => {
    if (isPortsPopoverOpen) {
      handleGetPortMappings();
    }
  }, [isPortsPopoverOpen, handleGetPortMappings]);

  useEffect(() => {
    if (!pendingCommand) return;

    const activeSession = sessions.find(s => s.id === activeTab);
    if (!activeSession) {
      setPendingCommand(null);
      return;
    }

    const terminalRef = terminalRefs.current[activeSession.id];
    if (terminalRef?.current?.sendCommand && activeSession.isConnected) {
      terminalRef.current.sendCommand(pendingCommand.command);
    }

    setPendingCommand(null);
  }, [pendingCommand, activeTab, sessions, setPendingCommand]);

  const toggleSplit = useCallback(() => {
    setIsSplit((prev) => {
      const next = !prev;
      if (next) {
        const other = sessions.find((s) => s.id !== activeTab);
        setSplitTabId((existing) =>
          existing && existing !== activeTab ? existing : other?.id ?? null
        );
      }
      return next;
    });
  }, [sessions, activeTab]);

  const handleSplitDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = splitContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const onMove = (me: MouseEvent) => {
        const pct = ((me.clientX - rect.left) / rect.width) * 100;
        setSplitRatio(Math.min(Math.max(pct, SPLIT_MIN), SPLIT_MAX));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    []
  );

  const isActiveAiTab = (sessionId: string): boolean =>
    sessionId === activeAiTerminalId;

  const aiSessionCount = sessions.filter(s => s.isReadOnly).length;

  const activeSession = sessions.find((s) => s.id === activeTab);
  const activeTerminalRef = activeSession ? terminalRefs.current[activeSession.id] : null;

  const actionButtons = activeSession && activeTerminalRef ? (
    <>
      <Popover open={isPortsPopoverOpen} onOpenChange={setIsPortsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
            title="View open ports"
            aria-label="View open ports"
          >
            <Network className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-56 p-0 bg-popover text-popover-foreground border-border shadow-lg"
          align="end"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/60 rounded-t-md">
            <span className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
              Open Ports
            </span>
          </div>
          <div className="px-3 py-2 space-y-1.5">
            {Object.keys(portMappings).length > 0 ? (
              Object.entries(portMappings).map(([containerPort, hostPort]) => {
                const portNum = containerPort.split('/')[0];
                const protocol = containerPort.split('/')[1] || 'tcp';
                return (
                  <div
                    key={containerPort}
                    className="flex items-center justify-between gap-3 text-xs font-mono text-foreground"
                  >
                    <span className="px-1.5 py-0.5 rounded bg-muted text-foreground">
                      {portNum}
                      <span className="text-[10px] ml-1 text-muted-foreground">/{protocol}</span>
                    </span>
                    <span className="text-[10px] text-muted-foreground">→</span>
                    <span className="px-1.5 py-0.5 rounded font-semibold bg-primary/15 text-primary border border-primary/30">
                      {hostPort}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-muted-foreground text-center py-2">
                No port mappings available
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Button onClick={() => activeTerminalRef.current?.clearTerminal?.()} variant="ghost" size="icon" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" title="Clear terminal" aria-label="Clear terminal"><Trash2 className="h-3.5 w-3.5" /></Button>
      {activeSession.isReadOnly && (
        <>
          <Button onClick={() => activeTerminalRef.current?.sendCommand?.('\x03')} variant="ghost" size="icon" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" title="Send Ctrl+C (interrupt)" aria-label="Send Ctrl+C"><StopCircle className="h-3.5 w-3.5" /></Button>
        </>
      )}
      <Button onClick={() => activeTerminalRef.current?.getSessions?.()} variant="ghost" size="icon" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground" title="Session info" aria-label="Session info"><Database className="h-3.5 w-3.5" /></Button>
      {!activeSession.isReadOnly && (
        <Popover open={isHistoryPopoverOpen} onOpenChange={setIsHistoryPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              title="Send terminal history to chat"
              aria-label="Send terminal history to chat"
            >
              <Upload className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-3 bg-popover border-border"
            align="end"
          >
            <div className="text-xs text-muted-foreground mb-2">
              Send terminal history to chat
            </div>
            <RadioGroup
              value={historyScope}
              onValueChange={(v) =>
                setHistoryScope(v as 'last' | 'all' | 'custom')
              }
              className="gap-2"
            >
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <RadioGroupItem value="last" id="history-scope-last" />
                <span>Last command + output</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <RadioGroupItem value="all" id="history-scope-all" />
                <span>All terminal history</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <RadioGroupItem value="custom" id="history-scope-custom" />
                <span>Last</span>
                <Input
                  type="number"
                  min={1}
                  max={HISTORY_MAX_N}
                  step={1}
                  value={historyCustomN}
                  onChange={(e) => setHistoryCustomN(e.target.value)}
                  onFocus={() => setHistoryScope('custom')}
                  aria-invalid={customInvalid}
                  aria-label="Number of previous commands"
                  className={cn(
                    'h-6 w-14 text-xs px-1 py-0',
                    customInvalid && 'border-destructive focus-visible:ring-destructive'
                  )}
                />
                <span>commands</span>
              </label>
            </RadioGroup>
            {customInvalid && (
              <p className="mt-2 text-[11px] text-destructive">
                Enter an integer between 1 and {HISTORY_MAX_N}.
              </p>
            )}
            <Button
              size="sm"
              disabled={sendDisabled}
              className="w-full mt-3 h-7 text-xs"
              onClick={() => {
                let scope: 'last' | 'all' | number;
                if (historyScope === 'custom') {
                  if (parsedCustomN === null) return;
                  scope = parsedCustomN;
                } else {
                  scope = historyScope;
                }
                const sent = activeTerminalRef.current?.sendHistoryToChat?.(scope) ?? 0;
                if (sent > 0) setIsHistoryPopoverOpen(false);
              }}
            >
              Send
            </Button>
          </PopoverContent>
        </Popover>
      )}
      <div className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
      <Button
        onClick={toggleSplit}
        variant="ghost"
        size="icon"
        disabled={sessions.length < 2}
        className={cn(
          'h-6 w-6 p-0',
          isSplit
            ? 'text-primary hover:text-primary bg-primary/15'
            : 'text-muted-foreground hover:text-foreground'
        )}
        title={
          sessions.length < 2
            ? 'Open a second terminal to enable split view'
            : isSplit
              ? 'Close split view'
              : 'Split view — show two terminals side by side'
        }
        aria-label="Toggle split view"
      >
        {isSplit ? <Square className="h-3.5 w-3.5" /> : <Columns2 className="h-3.5 w-3.5" />}
      </Button>
      {isSplit && (
        <Select
          value={splitTabId ?? ''}
          onValueChange={(v) => setSplitTabId(v)}
        >
          <SelectTrigger className="h-6 w-36 bg-muted/60 border-border text-foreground text-[11px] px-2">
            <SelectValue placeholder="Right pane" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {sessions
              .filter((s) => s.id !== activeTab)
              .map((s) => (
                <SelectItem
                  key={s.id}
                  value={s.id}
                  className="text-foreground text-xs"
                >
                  {s.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      )}
      <Button
        onClick={() => {
          if (activeSession.isConnected) {
            activeTerminalRef.current?.disconnectWebSocket?.();
          } else {
            activeTerminalRef.current?.forceReconnect?.();
          }
        }}
        variant="ghost"
        size="icon"
        className={cn(
          'h-6 w-6 p-0',
          activeSession.isConnected
            ? 'text-destructive hover:text-destructive hover:bg-destructive/15'
            : 'text-primary hover:text-primary hover:bg-primary/15'
        )}
        title={activeSession.isConnected ? 'Disconnect' : 'Connect'}
        aria-label={activeSession.isConnected ? 'Disconnect' : 'Connect'}
      >
        {activeSession.isConnected ? <WifiOff className="h-3.5 w-3.5" /> : <Wifi className="h-3.5 w-3.5" />}
      </Button>
    </>
  ) : null;

  const mobileActions = activeSession && activeTerminalRef ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground touch-manipulation"
          aria-label="Terminal actions"
        >
          <MoreVertical className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {activeSession.isReadOnly && (
          <DropdownMenuItem
            className="py-2.5"
            onClick={() => activeTerminalRef.current?.sendCommand?.('\x03')}
          >
            <StopCircle className="mr-2 h-4 w-4" />
            Send Ctrl+C
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="py-2.5"
          onClick={() => activeTerminalRef.current?.clearTerminal?.()}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Clear terminal
        </DropdownMenuItem>
        <DropdownMenuItem
          className="py-2.5"
          onClick={() => activeTerminalRef.current?.getSessions?.()}
        >
          <Database className="mr-2 h-4 w-4" />
          Session info
        </DropdownMenuItem>
        {!activeSession.isReadOnly && (
          <>
            <DropdownMenuItem
              className="py-2.5"
              onClick={() =>
                activeTerminalRef.current?.sendHistoryToChat?.('last')
              }
            >
              <Upload className="mr-2 h-4 w-4" />
              Send last command to chat
            </DropdownMenuItem>
            <DropdownMenuItem
              className="py-2.5"
              onClick={() =>
                activeTerminalRef.current?.sendHistoryToChat?.('all')
              }
            >
              <Upload className="mr-2 h-4 w-4" />
              Send all history to chat
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={cn(
            'py-2.5',
            activeSession.isConnected
              ? 'text-destructive focus:text-destructive'
              : 'text-primary focus:text-primary'
          )}
          onClick={() => {
            if (activeSession.isConnected) {
              activeTerminalRef.current?.disconnectWebSocket?.();
            } else {
              activeTerminalRef.current?.forceReconnect?.();
            }
          }}
        >
          {activeSession.isConnected ? (
            <WifiOff className="mr-2 h-4 w-4" />
          ) : (
            <Wifi className="mr-2 h-4 w-4" />
          )}
          {activeSession.isConnected ? 'Disconnect' : 'Connect'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Open ports
        </DropdownMenuLabel>
        {Object.keys(portMappings).length > 0 ? (
          Object.entries(portMappings).map(([containerPort, hostPort]) => {
            const portNum = containerPort.split('/')[0];
            const protocol = containerPort.split('/')[1] || 'tcp';
            return (
              <div
                key={containerPort}
                className="flex items-center justify-between gap-2 px-2 py-1 font-mono text-xs"
              >
                <span className="rounded bg-muted px-1.5 py-0.5">
                  {portNum}
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    /{protocol}
                  </span>
                </span>
                <span className="text-[10px] text-muted-foreground">→</span>
                <span className="rounded border border-primary/30 bg-primary/15 px-1.5 py-0.5 font-semibold text-primary">
                  {hostPort}
                </span>
              </div>
            );
          })
        ) : (
          <div className="px-2 py-1 text-xs text-muted-foreground">
            No port mappings
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  const computeSessionStyle = (sessionId: string): React.CSSProperties => {
    const isLeft = sessionId === activeTab;
    const isRight = isSplit && splitTabId === sessionId;
    if (!isSplit || (!isLeft && !isRight)) {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }
    if (isLeft) {
      return { left: 0, width: `${splitRatio}%`, top: 0, bottom: 0 };
    }
    return {
      right: 0,
      width: `${100 - splitRatio}%`,
      top: 0,
      bottom: 0,
      borderLeft: '1px solid hsl(var(--border))'
    };
  };

  const computeSessionVisibility = (sessionId: string): boolean => {
    if (!isSplit) return activeTab === sessionId;
    return activeTab === sessionId || splitTabId === sessionId;
  };

  return (
    <CardSquare className={cn('flex h-full flex-col bg-card border-border shadow-sm', className)}>
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
          <div className="flex flex-none items-center border-b border-border bg-card/85 pr-1 gap-1">
            <TabsList className="flex-1 min-w-0 justify-start bg-transparent rounded-none p-0 gap-0 min-h-fit py-0 flex items-center overflow-x-auto custom-scrollbar">
              {sessions.map((s) => {
                const isAi = s.isReadOnly;
                const isActive = isAi && isActiveAiTab(s.id);
                const isSelected = activeTab === s.id;
                const isInRightPane = isSplit && splitTabId === s.id;

                return (
                  <TabsTrigger
                    key={s.id}
                    value={s.id}
                    className={cn(
                      'flex shrink-0 items-center gap-1.5 px-2 py-1 rounded-t-md transition-colors font-medium text-[11px] min-w-0 border-b-2',
                      isSelected
                        ? cn(
                            'bg-accent shadow-sm text-foreground',
                            s.isConnecting
                              ? 'border-amber-500'
                              : s.isConnected
                                ? isActive
                                  ? 'border-primary'
                                  : 'border-primary/60'
                                : 'border-destructive'
                          )
                        : isActive
                          ? 'bg-primary/10 text-primary border-primary/50 hover:bg-primary/15'
                          : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground hover:bg-accent'
                    )}
                    aria-label={s.name}
                  >
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full mr-1',
                        s.isConnecting
                          ? 'bg-amber-500 animate-pulse'
                          : s.isConnected
                            ? 'bg-primary'
                            : 'bg-destructive'
                      )}
                      title={s.isConnecting ? 'Connecting' : s.isConnected ? 'Connected' : 'Disconnected'}
                    />
                    {isAi ? (
                      <Zap
                        className={cn(
                          'h-3.5 w-3.5',
                          isActive ? 'text-primary' : 'text-muted-foreground'
                        )}
                        aria-label="AI Terminal"
                      />
                    ) : (
                      <Monitor className="h-3.5 w-3.5 text-primary" aria-label="Terminal" />
                    )}
                    <span className="truncate">{s.name}</span>
                    {isInRightPane && (
                      <span className="ml-1 px-1 py-0.5 rounded text-[9px] font-bold uppercase bg-accent text-foreground border border-border">
                        split
                      </span>
                    )}
                    {s.isReadOnly && !isTaskStreaming && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionReadOnly(s.id);
                        }}
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 ml-1 hover:bg-primary/15 hover:text-primary border border-transparent transition-colors rounded flex items-center justify-center"
                        title="Take over — let user type in this terminal"
                        aria-label="Take over AI terminal"
                      >
                        <KeyRound className="h-3 w-3" />
                      </Button>
                    )}
                    {!s.isReadOnly && typeof s.id === 'string' && s.id.startsWith('ai-terminal') && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionReadOnly(s.id);
                        }}
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 ml-1 hover:bg-accent hover:text-foreground border border-transparent transition-colors rounded flex items-center justify-center"
                        title="Release back to AI (read-only)"
                        aria-label="Release terminal back to AI"
                      >
                        <Bot className="h-3 w-3" />
                      </Button>
                    )}
                    {((!isAi && sessions.length > 1) || (isAi && aiSessionCount > 1 && !isTaskStreaming)) && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSession(s.id);
                        }}
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 p-0 ml-1 hover:bg-destructive/15 hover:text-destructive border border-transparent transition-colors rounded flex items-center justify-center"
                        title="Close"
                        aria-label="Close tab"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <Button
              variant="ghost"
              onClick={addNewSession}
              className={cn(
                'flex shrink-0 items-center gap-1 ml-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors touch-manipulation',
                isMobile ? 'h-9 px-2.5' : 'px-1.5 py-0.5 h-6'
              )}
              title="New user terminal"
              aria-label="New user terminal"
            >
              <Plus className={isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
              <Monitor className={isMobile ? 'h-3.5 w-3.5' : 'h-3 w-3'} />
            </Button>
            {isMobile
              ? mobileActions && (
                  <div className="flex shrink-0 items-center ml-auto pl-1">
                    {mobileActions}
                  </div>
                )
              : actionButtons && (
                  <div className="flex shrink-0 items-center gap-0.5 ml-auto pl-2">
                    {actionButtons}
                  </div>
                )}
          </div>
          <div
            ref={splitContainerRef}
            className="flex-1 relative bg-card min-h-0"
          >
            {sessions.map((s) => {
              const visible = computeSessionVisibility(s.id);
              return (
                <div
                  key={s.isAiTerminal ? s.id : `${s.id}:${userTerminalEpoch}`}
                  style={computeSessionStyle(s.id)}
                  className={cn(
                    'absolute transition-[opacity,width,left,right] duration-300 ease-out',
                    visible
                      ? 'opacity-100 z-10'
                      : 'opacity-0 pointer-events-none z-0'
                  )}
                >
                  <Terminal
                    ref={terminalRefs.current[s.id]}
                    wsUrl={s.wsUrl}
                    className="h-full"
                    showHeader={false}
                    isReadOnly={s.isReadOnly}
                    initialTerminalId={s.id}
                    onConnect={() => handleConnect(s.id)}
                    onDisconnect={() => handleDisconnect(s.id)}
                    onConnecting={() => updateSessionStatus(s.id, { isConnecting: true })}
                    onReclaimControl={() => reclaimForAi(s.id)}
                    {...(s.isReadOnly && isActiveAiTab(s.id)
                      ? { onRegisterConnect: setAiTerminalConnect }
                      : {})}
                  />
                </div>
              );
            })}
            {isSplit && splitTabId && (
              <div
                onMouseDown={handleSplitDragStart}
                className="absolute top-0 bottom-0 z-20 w-2 -translate-x-1/2 cursor-col-resize group"
                style={{ left: `${splitRatio}%` }}
                aria-label="Resize split"
                role="separator"
              >
                <div className="mx-auto h-full w-[2px] bg-border group-hover:bg-primary transition-colors" />
              </div>
            )}
          </div>
        </Tabs>
      </CardContent>
    </CardSquare>
  );
};

export default TabbedTerminal;
